import { openDB } from "idb";
import type { ChatMessage, ProviderSecret, TraitSelection } from "@/types";

type CipherRecord = { iv: string; ciphertext: string };

const dbPromise = typeof indexedDB === "undefined"
  ? null
  : openDB("anisoul-local-v1", 1, {
      upgrade(database) {
        database.createObjectStore("secrets");
        database.createObjectStore("chats");
        database.createObjectStore("drafts");
      },
    });

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

async function keyFromSignature(signature: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(signature));
  return crypto.subtle.importKey("raw", digest, "AES-GCM", false, ["encrypt", "decrypt"]);
}

export const secretUnlockMessage = (address: string) =>
  `AniSoul local AI vault\n\nUnlock encrypted keys for ${address.toLowerCase()}\nOrigin: ${window.location.origin}\n\nThis signature does not create a transaction.`;

export async function saveProviderSecret(address: string, signature: string, secret: ProviderSecret) {
  if (!dbPromise) throw new Error("Browser storage is unavailable.");
  const key = await keyFromSignature(signature);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(JSON.stringify(secret)),
  );
  const record: CipherRecord = {
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  };
  const database = await dbPromise;
  await database.put("secrets", record, address.toLowerCase());
}

export async function loadProviderSecret(address: string, signature: string): Promise<ProviderSecret | null> {
  if (!dbPromise) return null;
  const database = await dbPromise;
  const record = (await database.get("secrets", address.toLowerCase())) as CipherRecord | undefined;
  if (!record) return null;
  const key = await keyFromSignature(signature);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(record.iv) },
    key,
    base64ToBytes(record.ciphertext),
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as ProviderSecret;
}

export async function hasProviderSecret(address: string) {
  if (!dbPromise) return false;
  const database = await dbPromise;
  return Boolean(await database.getKey("secrets", address.toLowerCase()));
}

export async function deleteProviderSecret(address: string) {
  if (!dbPromise) return;
  const database = await dbPromise;
  await database.delete("secrets", address.toLowerCase());
}

export async function loadChat(key: string): Promise<ChatMessage[]> {
  if (!dbPromise) return [];
  const database = await dbPromise;
  return ((await database.get("chats", key)) as ChatMessage[] | undefined) ?? [];
}

export async function saveChat(key: string, messages: ChatMessage[]) {
  if (!dbPromise) return;
  const database = await dbPromise;
  await database.put("chats", messages.slice(-80), key);
}

export async function loadDraft(address: string): Promise<TraitSelection | null> {
  if (!dbPromise) return null;
  const database = await dbPromise;
  return ((await database.get("drafts", address.toLowerCase())) as TraitSelection | undefined) ?? null;
}

export async function saveDraft(address: string, traits: TraitSelection) {
  if (!dbPromise) return;
  const database = await dbPromise;
  await database.put("drafts", traits, address.toLowerCase());
}
